'use client';

import { useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import StockCombinedChart from '../charts/StockCombinedChart';
import { StockDetailData, getStockDetail } from '@/lib/api/stockDetailApi';
import {
  ScreenerType,
  SCREENER_LABEL,
  meetsSmaGate,
} from '@/lib/screenerCriteria';

export interface StockDetailViewProps {
  isin: string;
  /**
   * Active screener — drives the SMA-comparison gate used when computing
   * "days meeting criteria" and bar coloring. Defaults to `big_buy`.
   */
  screener?: ScreenerType;
  /**
   * Optional content rendered in the right-hand side of the sticky header
   * (e.g. a close button when the view is hosted inside a modal).
   */
  headerAction?: ReactNode;
  /**
   * Override classes applied to the outer card wrapper. Useful for
   * adjusting max-width / scroll behaviour when the view is hosted as a
   * full page versus inside a modal.
   */
  cardClassName?: string;
}

interface DailyMetrics {
  date: string;
  dma_20?: number | null;
  dma_50?: number | null;
  dma_200: number;
  avg30_tq?: number | null;
  traded_qty: number;
  traded_amount: number;
  close_price: number;
  delivery_qty: number;
  delivery_amount: number;
  number_of_trades?: number;
  avg_trade_worth?: number;
  meets_criteria?: boolean;
  exceeds_max_criteria?: boolean;
}

const DEFAULT_CARD_CLASSNAME =
  'rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 max-w-7xl w-full';

export default function StockDetailView({
  isin,
  screener = 'big_buy',
  headerAction,
  cardClassName = DEFAULT_CARD_CLASSNAME,
}: StockDetailViewProps) {
  const [stockData, setStockData] = useState<StockDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deliveryThreshold, setDeliveryThreshold] = useState(40);
  const [minDeliveryAmountThreshold, setMinDeliveryAmountThreshold] = useState(50000000);
  const [maxDeliveryAmountThreshold, setMaxDeliveryAmountThreshold] = useState(100000000);
  const [showTooltip, setShowTooltip] = useState(true);
  const [showMetrics, setShowMetrics] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [rangeValue, setRangeValue] = useState<[number, number]>([0, 100]);

  useEffect(() => {
    let cancelled = false;
    const fetchStockDetails = async () => {
      try {
        setLoading(true);
        const data = await getStockDetail(isin);
        if (!cancelled) {
          setStockData(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching stock details:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch stock details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStockDetails();
    return () => {
      cancelled = true;
    };
  }, [isin]);

  const processedChartData = useMemo(() => {
    if (!stockData) return [] as DailyMetrics[];

    return stockData.chart_data
      .map((item) => {
        const deliveryPct = (item.delivery_qty / item.traded_qty) * 100;
        const tradedAmount = item.close_price * item.traded_qty;
        const deliveryAmount = item.close_price * item.delivery_qty;
        const avgTradeWorth = item.number_of_trades
          ? (item.close_price * item.traded_qty) / item.number_of_trades
          : 0;

        const passesSmaGate = meetsSmaGate(
          {
            close_price: item.close_price,
            traded_qty: item.traded_qty,
            delivery_qty: item.delivery_qty,
            dma_20: item.dma_20,
            dma_50: item.dma_50,
            dma_200: item.dma_200,
            avg30_tq: item.avg30_tq,
          },
          screener,
        );

        const meetsBasicCriteria = passesSmaGate && deliveryPct >= deliveryThreshold;
        const meetsMinMaxCriteria =
          meetsBasicCriteria &&
          deliveryAmount >= minDeliveryAmountThreshold &&
          deliveryAmount <= maxDeliveryAmountThreshold;
        const exceedsMaxCriteria =
          meetsBasicCriteria && deliveryAmount > maxDeliveryAmountThreshold;

        return {
          date: item.date,
          dma_20: item.dma_20,
          dma_50: item.dma_50,
          dma_200: item.dma_200,
          avg30_tq: item.avg30_tq,
          close_price: item.close_price,
          traded_qty: item.traded_qty,
          traded_amount: tradedAmount,
          delivery_qty: item.delivery_qty,
          delivery_amount: deliveryAmount,
          number_of_trades: item.number_of_trades,
          avg_trade_worth: avgTradeWorth,
          meets_criteria: meetsMinMaxCriteria,
          exceeds_max_criteria: exceedsMaxCriteria,
        } as DailyMetrics;
      })
      .reverse();
  }, [
    stockData,
    deliveryThreshold,
    minDeliveryAmountThreshold,
    maxDeliveryAmountThreshold,
    screener,
  ]);

  const chartDates = useMemo(() => {
    if (!processedChartData.length) return { earliestDate: null, latestDate: null, allDates: [] as Date[] };
    const sorted = processedChartData
      .map((item) => new Date(item.date))
      .sort((a, b) => a.getTime() - b.getTime());
    return { earliestDate: sorted[0], latestDate: sorted[sorted.length - 1], allDates: sorted };
  }, [processedChartData]);

  useEffect(() => {
    if (chartDates.earliestDate && chartDates.latestDate && (!dateRange[0] || !dateRange[1])) {
      setDateRange([chartDates.earliestDate, chartDates.latestDate]);
      setRangeValue([0, 100]);
    }
  }, [chartDates, dateRange]);

  const filteredChartData = useMemo(() => {
    if (!dateRange[0] || !dateRange[1] || !processedChartData.length) return processedChartData;
    const startDate = dateRange[0].getTime();
    const endDate = dateRange[1].getTime();
    return processedChartData.filter((item) => {
      const itemDate = new Date(item.date).getTime();
      return itemDate >= startDate && itemDate <= endDate;
    });
  }, [processedChartData, dateRange]);

  const sliderValueToDate = useCallback(
    (value: number): Date | null => {
      if (!chartDates.allDates.length || value < 0 || value > 100) return null;
      if (value === 0) return chartDates.earliestDate;
      if (value === 100) return chartDates.latestDate;
      const dateIndex = Math.floor((chartDates.allDates.length - 1) * (value / 100));
      return chartDates.allDates[dateIndex];
    },
    [chartDates],
  );

  const handleDateRangeChange = (values: [number, number]) => {
    setRangeValue(values);
    const startDate = sliderValueToDate(values[0]);
    const endDate = sliderValueToDate(values[1]);
    if (startDate && endDate) setDateRange([startDate, endDate]);
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const daysInRange = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return processedChartData.length;
    const diff = Math.abs(dateRange[1].getTime() - dateRange[0].getTime());
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
  }, [dateRange, processedChartData.length]);

  const barPercentage = useMemo(() => {
    const totalDays = processedChartData.length;
    const selectedDays = filteredChartData.length;
    const basePercentage = 0.6;
    if (selectedDays >= totalDays || selectedDays <= 1) return basePercentage;
    const ratio = totalDays / selectedDays;
    return Math.min(0.9, Math.max(0.6, basePercentage * ratio));
  }, [filteredChartData.length, processedChartData.length]);

  const daysMatchingCriteria = filteredChartData.filter((d) => d.meets_criteria).length;
  const daysExceedingMaxCriteria = filteredChartData.filter((d) => d.exceeds_max_criteria).length;

  const totalTradedAmount = filteredChartData
    .filter((d) => d.meets_criteria)
    .reduce((sum, d) => sum + (d.traded_amount || 0), 0);

  const totalExceedingTradedAmount = filteredChartData
    .filter((d) => d.exceeds_max_criteria)
    .reduce((sum, d) => sum + (d.traded_amount || 0), 0);

  const maxDeliveryAmountInData =
    processedChartData.length > 0
      ? Math.max(...processedChartData.map((d) => d.delivery_amount || 0)) * 1.2
      : 10000000;

  const getSliderMaxValue = () => {
    const logMax = Math.log10(maxDeliveryAmountInData);
    return Math.max(Math.ceil(logMax) * 10 + 20, 80);
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return `₹${formatNumber(value)}`;
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    const isNegative = value < 0;
    const abs = Math.abs(value);
    const sign = isNegative ? '-' : '';
    if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)} L`;
    if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(2)}K`;
    return `${sign}${abs.toFixed(2)}`;
  };

  const getValueColor = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'text-zinc-500';
    if (value > 0) return 'text-green-500';
    if (value < 0) return 'text-red-500';
    return 'text-zinc-500';
  };

  const handleDeliveryThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeliveryThreshold(parseInt(e.target.value, 10));
  };

  const minDeliveryAmountSliderValue = () => {
    if (minDeliveryAmountThreshold <= 0) return 0;
    return Math.log10(minDeliveryAmountThreshold) * 10;
  };

  const maxDeliveryAmountSliderValue = () => {
    if (maxDeliveryAmountThreshold <= 0) return 0;
    return Math.log10(maxDeliveryAmountThreshold) * 10;
  };

  const sliderToDeliveryAmountValue = (sliderValue: number) =>
    Math.floor(Math.pow(10, sliderValue / 10));

  const handleMinDeliveryAmountSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sliderValue = parseFloat(e.target.value);
    const newMin = sliderToDeliveryAmountValue(sliderValue);
    setMinDeliveryAmountThreshold(Math.min(newMin, maxDeliveryAmountThreshold - 1));
  };

  const handleMaxDeliveryAmountSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sliderValue = parseFloat(e.target.value);
    const newMax = sliderToDeliveryAmountValue(sliderValue);
    setMaxDeliveryAmountThreshold(Math.max(newMax, minDeliveryAmountThreshold + 1));
  };

  const toggleTooltip = () => setShowTooltip((v) => !v);

  if (loading) {
    return (
      <div className={cardClassName}>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  if (error || !stockData) {
    return (
      <div className={cardClassName}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-red-500">Error</h2>
          {headerAction}
        </div>
        <p className="text-zinc-600 dark:text-zinc-300">{error || 'Failed to load stock details'}</p>
      </div>
    );
  }

  const { stock, financial_data } = stockData;
  const latestMetrics = processedChartData.length > 0 ? processedChartData[processedChartData.length - 1] : null;

  return (
    <div className={cardClassName}>
      <div className="flex justify-between items-center mb-4 sticky top-0 bg-inherit pt-2 pb-3 z-[2]">
        <div>
          <h2 className="text-2xl font-bold text-zinc-800 dark:text-white">{stock.stock_name}</h2>
          <div className="flex items-center space-x-2 text-sm text-zinc-500 dark:text-zinc-400">
            <span>{stock.nse_code}</span>
            {stock.bse_code && (
              <>
                <span>•</span>
                <span>BSE: {stock.bse_code}</span>
              </>
            )}
            <span>•</span>
            <span>{stock.industry_name}</span>
            <span>•</span>
            <span>{stock.sector_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMetrics(!showMetrics)}
            className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Toggle Metrics"
          >
            <span className="text-xs px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-zinc-600 dark:text-zinc-300">
              {showMetrics ? 'Hide Metrics' : 'Show Metrics'}
            </span>
          </button>
          {headerAction}
        </div>
      </div>

      {showMetrics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
            <div className="flex flex-col">
              <div className="mb-2">
                <span className="text-3xl font-bold text-zinc-900 dark:text-white">
                  {formatCurrency(financial_data.current_price)}
                </span>
                {latestMetrics && (
                  <span
                    className={`ml-2 text-lg font-medium ${getValueColor(financial_data.current_price - latestMetrics.close_price)}`}
                  >
                    {formatPercent(((financial_data.current_price - latestMetrics.close_price) / latestMetrics.close_price) * 100)}
                  </span>
                )}
              </div>
              <span className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">Latest pricing data</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">1M Return</span>
                <span className={`text-sm font-medium ${getValueColor(financial_data.return_over_1month)}`}>
                  {formatPercent(financial_data.return_over_1month)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">3M Return</span>
                <span className={`text-sm font-medium ${getValueColor(financial_data.return_over_3months)}`}>
                  {formatPercent(financial_data.return_over_3months)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">6M Return</span>
                <span className={`text-sm font-medium ${getValueColor(financial_data.return_over_6months)}`}>
                  {formatPercent(financial_data.return_over_6months)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">1Y Return</span>
                <span className={`text-sm font-medium ${getValueColor(financial_data.return_over_1year)}`}>
                  {formatPercent(financial_data.return_over_1year)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">Down from 52W High</span>
                <span className="text-sm font-medium text-red-500">{formatPercent(financial_data.down_from_52w_high)}</span>
              </div>
              {latestMetrics && (
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-600 dark:text-zinc-300">Daily Volume</span>
                  <span className="text-sm font-medium text-zinc-900 dark:text-white">{formatNumber(latestMetrics.traded_qty)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
            <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 mb-3">Valuation Metrics</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">Market Cap</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">{formatNumber(financial_data.market_capitalization)} Cr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">P/E Ratio</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  {financial_data.price_to_earning ? financial_data.price_to_earning.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">EPS</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  ₹{financial_data.eps ? financial_data.eps.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">P/B Ratio</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  {financial_data.price_to_book_value ? financial_data.price_to_book_value.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">P/S Ratio</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  {financial_data.price_to_sales ? financial_data.price_to_sales.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">Earnings Yield</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">{formatPercent(financial_data.earnings_yield)}</span>
              </div>
            </div>
          </div>

          <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
            <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 mb-3">Financial Metrics</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">Promoter Holding</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">{formatPercent(financial_data.promoter_holding)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">ROE</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">{formatPercent(financial_data.return_on_equity)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">ROCE</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">{formatPercent(financial_data.return_on_capital_employed)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-300">Debt/Equity</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-white">
                  {financial_data.debt_to_equity ? financial_data.debt_to_equity.toFixed(2) : 'N/A'}
                </span>
              </div>
              {financial_data.free_float_shares && (
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-600 dark:text-zinc-300">Free Float %</span>
                  <span className="text-sm font-medium text-zinc-900 dark:text-white">
                    {formatPercent((financial_data.free_float_shares / (financial_data.total_shares || 1)) * 100)}
                  </span>
                </div>
              )}
              {financial_data.free_float_mcap && (
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-600 dark:text-zinc-300">Free Float MCap</span>
                  <span className="text-sm font-medium text-zinc-900 dark:text-white">
                    {formatNumber(financial_data.free_float_mcap)} Cr
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 mb-6">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
            <h3 className="text-lg font-medium text-zinc-900 dark:text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>Stock Price Analysis</span>
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 dark:text-emerald-400"
                  title="Criteria used to highlight days in the chart"
                >
                  {SCREENER_LABEL[screener]}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {daysMatchingCriteria > 0 && (
                  <div className="text-sm font-normal text-yellow-600 dark:text-yellow-400">
                    {daysMatchingCriteria} days met criteria
                  </div>
                )}
                {daysExceedingMaxCriteria > 0 && (
                  <div className="text-sm font-normal text-purple-600 dark:text-purple-400">
                    {daysExceedingMaxCriteria} days exceeded max
                  </div>
                )}
              </div>
            </h3>
          </div>

          <div className="p-4">
            <div className="flex flex-wrap gap-3 mb-4">
              {daysMatchingCriteria > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-2 border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      {daysMatchingCriteria} days met criteria - Total: {formatCurrency(totalTradedAmount)}
                    </span>
                  </div>
                </div>
              )}
              {daysExceedingMaxCriteria > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
                    <span className="text-sm font-medium text-purple-800 dark:text-purple-300">
                      {daysExceedingMaxCriteria} days exceeded max - Total: {formatCurrency(totalExceedingTradedAmount)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-zinc-50 dark:bg-zinc-800/60 p-3 rounded-xl mb-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="delivery-threshold" className="text-sm text-zinc-600 dark:text-zinc-400">
                        Min Delivery %:
                      </label>
                      <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 px-2 py-0.5 rounded">
                        {deliveryThreshold}%
                      </span>
                    </div>
                    <input
                      id="delivery-threshold"
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={deliveryThreshold}
                      onChange={handleDeliveryThresholdChange}
                      className="w-full h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm text-zinc-600 dark:text-zinc-400">Delivery Amount Range:</label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 px-2 py-0.5 rounded">
                          Min: {formatNumber(minDeliveryAmountThreshold)}
                        </span>
                        <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 px-2 py-0.5 rounded">
                          Max: {formatNumber(maxDeliveryAmountThreshold)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="relative">
                        <input
                          id="min-delivery-amount-threshold"
                          type="range"
                          min="0"
                          max={getSliderMaxValue()}
                          step="1"
                          value={minDeliveryAmountSliderValue()}
                          onChange={handleMinDeliveryAmountSliderChange}
                          className="w-full h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                        />
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Min Threshold (yellow bars)</div>
                      </div>
                      <div className="relative">
                        <input
                          id="max-delivery-amount-threshold"
                          type="range"
                          min="0"
                          max={getSliderMaxValue()}
                          step="1"
                          value={maxDeliveryAmountSliderValue()}
                          onChange={handleMaxDeliveryAmountSliderChange}
                          className="w-full h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                        />
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Max Threshold (purple bars above this)</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm text-zinc-600 dark:text-zinc-400">Date Range:</label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 px-2 py-0.5 rounded">
                        {formatDate(dateRange[0])}
                      </span>
                      <span className="text-xs text-zinc-500">to</span>
                      <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 bg-white dark:bg-zinc-900 px-2 py-0.5 rounded">
                        {formatDate(dateRange[1])}
                      </span>
                      <span className="text-xs text-zinc-500">({daysInRange} days)</span>
                    </div>
                  </div>

                  <div className="px-2 py-4">
                    <div className="relative h-8">
                      <div className="absolute top-1/2 -translate-y-1/2 inset-x-0 h-2 rounded-full bg-zinc-200 dark:bg-zinc-700"></div>
                      <div
                        className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-emerald-500"
                        style={{ left: `${rangeValue[0]}%`, right: `${100 - rangeValue[1]}%` }}
                      ></div>
                      <button
                        type="button"
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-white hover:bg-emerald-50 active:scale-110 border-2 border-emerald-500 rounded-full cursor-pointer transition-all shadow focus:outline-none focus:ring-2 focus:ring-emerald-500/40 z-20"
                        style={{ left: `${rangeValue[0]}%` }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const slider = e.currentTarget.parentElement;
                          if (!slider) return;
                          const rect = slider.getBoundingClientRect();
                          const onMove = (ev: MouseEvent) => {
                            const offsetX = ev.clientX - rect.left;
                            const v = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
                            if (v < rangeValue[1] - 1) handleDateRangeChange([v, rangeValue[1]]);
                          };
                          const onUp = () => {
                            document.removeEventListener('mousemove', onMove);
                            document.removeEventListener('mouseup', onUp);
                          };
                          document.addEventListener('mousemove', onMove);
                          document.addEventListener('mouseup', onUp);
                        }}
                        aria-label="Set start date"
                      >
                        <span className="sr-only">Start date</span>
                      </button>
                      <button
                        type="button"
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-white hover:bg-emerald-50 active:scale-110 border-2 border-emerald-500 rounded-full cursor-pointer transition-all shadow focus:outline-none focus:ring-2 focus:ring-emerald-500/40 z-20"
                        style={{ left: `${rangeValue[1]}%` }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const slider = e.currentTarget.parentElement;
                          if (!slider) return;
                          const rect = slider.getBoundingClientRect();
                          const onMove = (ev: MouseEvent) => {
                            const offsetX = ev.clientX - rect.left;
                            const v = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
                            if (v > rangeValue[0] + 1) handleDateRangeChange([rangeValue[0], v]);
                          };
                          const onUp = () => {
                            document.removeEventListener('mousemove', onMove);
                            document.removeEventListener('mouseup', onUp);
                          };
                          document.addEventListener('mousemove', onMove);
                          document.addEventListener('mouseup', onUp);
                        }}
                        aria-label="Set end date"
                      >
                        <span className="sr-only">End date</span>
                      </button>
                      <div
                        className="absolute -translate-x-1/2 top-0 -mt-6 px-2 py-1 bg-zinc-800 text-white text-xs rounded whitespace-nowrap pointer-events-none"
                        style={{ left: `${rangeValue[0]}%` }}
                      >
                        {formatDate(dateRange[0])}
                      </div>
                      <div
                        className="absolute -translate-x-1/2 top-0 -mt-6 px-2 py-1 bg-zinc-800 text-white text-xs rounded whitespace-nowrap pointer-events-none"
                        style={{ left: `${rangeValue[1]}%` }}
                      >
                        {formatDate(dateRange[1])}
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 px-2 text-xs text-zinc-500 dark:text-zinc-400">
                      <span>{formatDate(chartDates.earliestDate)}</span>
                      <span>{formatDate(chartDates.latestDate)}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => handleDateRangeChange([0, 100])}
                      className="text-xs px-2 py-1 rounded-lg border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
                    >
                      All Time
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (chartDates.allDates.length) {
                          const last3 = new Date(chartDates.latestDate!);
                          last3.setMonth(last3.getMonth() - 3);
                          const idx = chartDates.allDates.findIndex((d) => d >= last3);
                          if (idx >= 0) {
                            const pct = (idx / (chartDates.allDates.length - 1)) * 100;
                            handleDateRangeChange([pct, 100]);
                          }
                        }
                      }}
                      className="text-xs px-2 py-1 rounded-lg border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
                    >
                      Last 3 Months
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (chartDates.allDates.length) {
                          const last1 = new Date(chartDates.latestDate!);
                          last1.setMonth(last1.getMonth() - 1);
                          const idx = chartDates.allDates.findIndex((d) => d >= last1);
                          if (idx >= 0) {
                            const pct = (idx / (chartDates.allDates.length - 1)) * 100;
                            handleDateRangeChange([pct, 100]);
                          }
                        }
                      }}
                      className="text-xs px-2 py-1 rounded-lg border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
                    >
                      Last Month
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (chartDates.allDates.length) {
                          const lastWeek = new Date(chartDates.latestDate!);
                          lastWeek.setDate(lastWeek.getDate() - 7);
                          const idx = chartDates.allDates.findIndex((d) => d >= lastWeek);
                          if (idx >= 0) {
                            const pct = (idx / (chartDates.allDates.length - 1)) * 100;
                            handleDateRangeChange([pct, 100]);
                          }
                        }
                      }}
                      className="text-xs px-2 py-1 rounded-lg border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
                    >
                      Last Week
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="h-[500px] relative">
              <StockCombinedChart
                dailyData={filteredChartData}
                stockName={stock.stock_name}
                deliveryThreshold={deliveryThreshold}
                minDeliveryAmountThreshold={minDeliveryAmountThreshold}
                maxDeliveryAmountThreshold={maxDeliveryAmountThreshold}
                barPercentage={barPercentage}
                showTooltip={showTooltip}
                screener={screener}
              />
              <div className="absolute top-12 right-3">
                <button
                  onClick={toggleTooltip}
                  className="text-xs px-2 py-1 rounded-lg bg-white/90 dark:bg-zinc-900/90 text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  {showTooltip ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
            <h3 className="text-lg font-medium text-zinc-900 dark:text-white">
              Weekly Trading Data (Meeting Criteria)
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-zinc-50 dark:bg-zinc-800/60">
                <tr>
                  <th className="px-4 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">Week Range</th>
                  <th className="px-4 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">Total Traded Amount</th>
                  <th className="px-4 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">Total Delivery Amount</th>
                  <th className="px-4 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">Delivery %</th>
                  <th className="px-4 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400">Criteria Met</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {(() => {
                  const weeklyData: {
                    startDate: Date;
                    endDate: Date;
                    totalTradedAmount: number;
                    totalDeliveryAmount: number;
                    totalTradedQty: number;
                    totalDeliveryQty: number;
                    criteriaType: 'none' | 'within-range' | 'exceeds-max';
                  }[] = [];

                  const sortedData = [...processedChartData].sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
                  );

                  let currentWeekStart: Date | null = null;
                  let currentWeekEnd: Date | null = null;
                  let currentWeekTradedAmount = 0;
                  let currentWeekDeliveryAmount = 0;
                  let currentWeekTradedQty = 0;
                  let currentWeekDeliveryQty = 0;
                  let hasCriteriaThisWeek = false;
                  let hasExceedMaxCriteriaThisWeek = false;

                  sortedData.forEach((item) => {
                    const itemDate = new Date(item.date);
                    const weekStart = new Date(itemDate);
                    weekStart.setDate(itemDate.getDate() - itemDate.getDay());
                    weekStart.setHours(0, 0, 0, 0);
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + 6);
                    weekEnd.setHours(23, 59, 59, 999);

                    if (!currentWeekStart || weekStart.getTime() !== currentWeekStart.getTime()) {
                      if (currentWeekStart && (hasCriteriaThisWeek || hasExceedMaxCriteriaThisWeek)) {
                        weeklyData.push({
                          startDate: currentWeekStart,
                          endDate: currentWeekEnd!,
                          totalTradedAmount: currentWeekTradedAmount,
                          totalDeliveryAmount: currentWeekDeliveryAmount,
                          totalTradedQty: currentWeekTradedQty,
                          totalDeliveryQty: currentWeekDeliveryQty,
                          criteriaType: hasExceedMaxCriteriaThisWeek
                            ? 'exceeds-max'
                            : hasCriteriaThisWeek
                              ? 'within-range'
                              : 'none',
                        });
                      }
                      currentWeekStart = weekStart;
                      currentWeekEnd = weekEnd;
                      currentWeekTradedAmount = 0;
                      currentWeekDeliveryAmount = 0;
                      currentWeekTradedQty = 0;
                      currentWeekDeliveryQty = 0;
                      hasCriteriaThisWeek = false;
                      hasExceedMaxCriteriaThisWeek = false;
                    }

                    if (item.meets_criteria || item.exceeds_max_criteria) {
                      currentWeekTradedAmount += item.traded_amount || 0;
                      currentWeekDeliveryAmount += item.delivery_amount || 0;
                      currentWeekTradedQty += item.traded_qty;
                      currentWeekDeliveryQty += item.delivery_qty;
                      if (item.meets_criteria) hasCriteriaThisWeek = true;
                      if (item.exceeds_max_criteria) hasExceedMaxCriteriaThisWeek = true;
                    }
                  });

                  if (currentWeekStart && (hasCriteriaThisWeek || hasExceedMaxCriteriaThisWeek)) {
                    weeklyData.push({
                      startDate: currentWeekStart,
                      endDate: currentWeekEnd!,
                      totalTradedAmount: currentWeekTradedAmount,
                      totalDeliveryAmount: currentWeekDeliveryAmount,
                      totalTradedQty: currentWeekTradedQty,
                      totalDeliveryQty: currentWeekDeliveryQty,
                      criteriaType: hasExceedMaxCriteriaThisWeek
                        ? 'exceeds-max'
                        : hasCriteriaThisWeek
                          ? 'within-range'
                          : 'none',
                    });
                  }

                  const weeksWithCriteria = weeklyData
                    .filter((week) => week.criteriaType !== 'none')
                    .slice(0, 10);

                  return weeksWithCriteria.map((week, index) => {
                    const weeklyDeliveryPercent =
                      week.totalTradedQty > 0
                        ? (week.totalDeliveryQty / week.totalTradedQty) * 100
                        : 0;

                    const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
                    const startDateStr = week.startDate.toLocaleDateString('en-US', formatOptions);
                    const endDateStr = week.endDate.toLocaleDateString('en-US', formatOptions);

                    const rowColorClass =
                      week.criteriaType === 'exceeds-max'
                        ? 'hover:bg-purple-50 dark:hover:bg-purple-900/20'
                        : 'hover:bg-yellow-50 dark:hover:bg-yellow-900/20';

                    const criteriaText = week.criteriaType === 'exceeds-max' ? 'Exceeds Max' : 'Within Range';
                    const criteriaColorClass =
                      week.criteriaType === 'exceeds-max'
                        ? 'text-purple-600 dark:text-purple-400 font-medium'
                        : 'text-yellow-600 dark:text-yellow-400 font-medium';

                    return (
                      <tr key={index} className={rowColorClass}>
                        <td className="px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100">
                          {startDateStr} - {endDateStr}
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100">{formatNumber(week.totalTradedAmount)}</td>
                        <td className="px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100">{formatNumber(week.totalDeliveryAmount)}</td>
                        <td
                          className={`px-4 py-3 text-sm ${
                            weeklyDeliveryPercent >= deliveryThreshold
                              ? 'text-green-600 dark:text-green-400 font-medium'
                              : 'text-zinc-900 dark:text-zinc-100'
                          }`}
                        >
                          {weeklyDeliveryPercent.toFixed(2)}%
                        </td>
                        <td className={`px-4 py-3 text-sm ${criteriaColorClass}`}>{criteriaText}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>

            {processedChartData.filter((item) => item.meets_criteria || item.exceeds_max_criteria).length === 0 && (
              <div className="py-8 text-center text-zinc-500 dark:text-zinc-400">
                No data meets criteria. Try adjusting the thresholds.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
